import { Button, ButtonProps } from 'antd'
import { FunctionComponent } from 'react'
import styled from 'styled-components'
import { Icon } from '@/components/custom-icon'
import UnlockSnapshotTooltip from '@/components/kol/detail/unlock-snapshot-tooltip'
import useAvailableUnlockData from '@/components/kol/hooks/use-available-unlock-data'
import useHandleClickUnlockButton from '@/components/kol/hooks/use-handle-click-unlock-button'
import { VipOnlyModal } from '@/components/modal/vip-only-modal'
import useIsJapan from '@/hooks/use-is-japan'
import { useUnlockKolDetailPermission } from '@/hooks/use-permission'
import { usePermissions } from '@/hooks/use-permissions'
import { I18nId } from '@/i18n/config'
import { useIntl } from '@/i18n/hooks/use-intl'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { toVoidFunction } from '@/utils/to-void-function'

interface UnlockButtonProps extends Pick<ButtonProps, 'size'> {
  kolInfo?: KolSchemaModel
  buttonI18nId: I18nId
  buttonFontWeight?: number
  from?: string
  isClickedFromDrawer?: boolean
}

const UnlockButton: FunctionComponent<UnlockButtonProps> = ({
  kolInfo,
  buttonI18nId,
  buttonFontWeight,
  size,
  from,
  isClickedFromDrawer,
}) => {
  const { formatMessage } = useIntl()
  const isJapan = useIsJapan()
  const { withFreePermission } = usePermissions()
  const unlockPermission = useUnlockKolDetailPermission()
  const { availableUnlockData, isSuccess } = useAvailableUnlockData(kolInfo?.id)
  const {
    handleClickUnlock,
    isUnlocking,
    isVipOnlyModalOpen,
    setIsVipOnlyModalOpen,
  } = useHandleClickUnlockButton(from, isClickedFromDrawer)
  const loadingAvailable = !isSuccess && !availableUnlockData

  const buttonText = formatMessage({
    id:
      isJapan && withFreePermission
        ? 'vip:plan_year_contact_btn'
        : buttonI18nId,
  })

  return (
    <Wrapper>
      <UnlockSnapshotTooltip
        availableUnlockData={availableUnlockData?.data.statistics_status}
        placement='bottomRight'
      >
        <StyledButton
          disabled={!unlockPermission}
          fontWeight={buttonFontWeight}
          icon={<Icon type='lock' />}
          loading={isUnlocking || loadingAvailable}
          size={size}
          type='primary'
          onClick={toVoidFunction(async (): Promise<void> => {
            if (!kolInfo) {
              return
            }

            await handleClickUnlock(kolInfo)
          })}
        >
          {loadingAvailable
            ? formatMessage({ id: 'general:loading' })
            : buttonText}
        </StyledButton>
      </UnlockSnapshotTooltip>
      <VipOnlyModal
        canContinueBrowsingFromProp
        onlyQuota
        source='Unlock KD Data'
        title={formatMessage(
          {
            id: 'kol:description_kol_info_quota_not_enough',
          },
          { br: <br /> },
        )}
        visible={isVipOnlyModalOpen}
        onClose={(): void => {
          setIsVipOnlyModalOpen(false)
        }}
      />
    </Wrapper>
  )
}

const StyledButton = styled(Button)<{ fontWeight?: number }>`
  &,
  &:hover,
  &:disabled {
    color: ${({ theme }): string => theme.colors.text.white};
    background: ${({ theme }): string => theme.colors.plan.premium} !important;
    border-color: ${({ theme }): string => theme.colors.plan.premium};
    font-weight: ${({ fontWeight }): number => fontWeight ?? 600};
  }
`

const Wrapper = styled.div``

export default UnlockButton
